import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";
import AppDateField from "./AppDateField";
import { AppMultiSelect } from "./AppSelectField";
import BTNLoader from "./BTNLoader";
import errorHandler from "../../utils/errorHandler";

/**
 * Shared export modal — date range, multi-select headers, CSV format.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {{ label: string, value: string }[]} props.headerOptions
 * @param {boolean} [props.showDateRange=true]
 * @param {object} [props.defaultFilters] - current page filters forwarded to the export API
 * @param {(params: object) => Promise<void>} props.onExport
 */
const AppExportModal = ({
  isOpen,
  onClose,
  title = "Export",
  headerOptions = [],
  showDateRange = true,
  defaultFilters = {},
  onExport,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [dateError, setDateError] = React.useState("");
  const [values, setValues] = React.useState({
    dateFrom: null,
    dateTo: null,
    headers: [],
    format: "csv",
  });

  React.useEffect(() => {
    if (!isOpen) return;
    setValues({
      dateFrom: null,
      dateTo: null,
      headers: [],
      format: "csv",
    });
    setDateError("");
  }, [isOpen]);

  const SELECT_ALL_VALUE = "__select_all__";
  const allHeaderValues = headerOptions.map((item) => item.value);
  const allSelected = allHeaderValues.length > 0 && allHeaderValues.every((value) => values.headers.includes(value));

  const headerSelectItems = [{ label: "Select All", value: SELECT_ALL_VALUE }, ...headerOptions];

  const handleHeadersChange = (event) => {
    const selected = event.target.value;
    const nextValues = typeof selected === "string" ? selected.split(",") : selected;

    if (nextValues.includes(SELECT_ALL_VALUE)) {
      handleChange("headers", allSelected ? [] : allHeaderValues);
      return;
    }

    handleChange(
      "headers",
      nextValues.filter((value) => value !== SELECT_ALL_VALUE),
    );
  };

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (name === "dateFrom" || name === "dateTo") setDateError("");
  };

  const validateDates = () => {
    if (!showDateRange) return true;
    const { dateFrom, dateTo } = values;
    if (dateFrom && !dateTo) {
      setDateError("Date to is required when Date from is set");
      return false;
    }
    if (dateTo && !dateFrom) {
      setDateError("Date from is required when Date to is set");
      return false;
    }
    if (dateFrom && dateTo && moment(dateTo).isBefore(moment(dateFrom), "day")) {
      setDateError("Date to must be on or after Date from");
      return false;
    }
    return true;
  };

  const handleExport = async () => {
    if (!validateDates()) return;
    if (!values.headers?.length) {
      toast.error("Select at least one header to export");
      return;
    }

    try {
      setIsLoading(true);
      const params = {
        ...defaultFilters,
        format: values.format,
        headers: values.headers.join(","),
      };

      if (showDateRange && values.dateFrom && values.dateTo) {
        params.dateFrom = moment(values.dateFrom).format("YYYY-MM-DD");
        params.dateTo = moment(values.dateTo).format("YYYY-MM-DD");
      }

      // Drop empty filter values so the request stays clean.
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] == null || params[key] === "ALL") {
          delete params[key];
        }
      });

      await onExport?.(params);
      onClose?.();
      toast.success("Export started successfully");
    } catch (error) {
      errorHandler(error?.response || error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, fontWeight: 700 }}>
        {title}
        <IconButton onClick={onClose} size="small" aria-label="Close export dialog">
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {showDateRange && (
          <Box mb={2.5}>
            <Box display="flex" gap={2}>
              <AppDateField
                label="Date from"
                value={values.dateFrom}
                maxDate={values.dateTo || undefined}
                onChange={(value) => handleChange("dateFrom", value)}
              />
              <AppDateField
                label="Date to"
                value={values.dateTo}
                minDate={values.dateFrom || undefined}
                onChange={(value) => handleChange("dateTo", value)}
              />
            </Box>
            {dateError ? <FormHelperText error>{dateError}</FormHelperText> : null}
          </Box>
        )}

        <Box mb={2.5}>
          <AppMultiSelect
            displayEmpty
            value={values.headers}
            onChange={handleHeadersChange}
            items={headerSelectItems}
            isOptionChecked={(item, selected) => {
              if (item.value === SELECT_ALL_VALUE) return allSelected;
              return selected.includes(item.value);
            }}
            renderValue={(selected) => {
              if (!selected?.length) return "Select Headers";
              if (allSelected) return "All headers selected";
              return selected
                .filter((value) => value !== SELECT_ALL_VALUE)
                .map((value) => headerOptions.find((item) => item.value === value)?.label ?? value)
                .join(", ");
            }}
          />
        </Box>

        <Box mb={1}>
          <Select
            fullWidth
            size="small"
            value={values.format}
            disabled
            onChange={(e) => handleChange("format", e.target.value)}
            sx={{ bgcolor: "#F3F4F6" }}
          >
            <MenuItem value="csv">CSV</MenuItem>
          </Select>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          disabled={isLoading}
          onClick={handleExport}
          startIcon={<BTNLoader isLoading={isLoading} size={16} color="inherit" />}
          sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }}
        >
          {isLoading ? "Exporting..." : "Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppExportModal;

/** Column header options per export entity (mapped to API `headers` query values). */
export const EXPORT_HEADER_OPTIONS = {
  users: [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Phone", value: "phone" },
    { label: "Sign-up platform", value: "signupPlatform" },
    { label: "Last signed in", value: "lastSignedIn" },
  ],
  organizations: [
    { label: "Client", value: "client" },
    { label: "Email", value: "email" },
    { label: "Join date", value: "joinDate" },
    { label: "Number", value: "number" },
    { label: "signup platform", value: "signupPlatform" },
    { label: "Organizations", value: "organizations" },
    { label: "Status", value: "status" },
    { label: "Members", value: "members" },
  ],
  referrals: [
    { label: "Client", value: "client" },
    { label: "Email", value: "email" },
    { label: "Join date", value: "joinDate" },
    { label: "Paid Invitees", value: "paidInvitees" },
    { label: "Total Invited", value: "totalInvited" },
  ],
};
